import { createFileRoute, redirect } from "@tanstack/react-router";

import { getCurrentUser } from "@/api/functions/auth";
import { LogoutPage } from "@/components/logout-page";

// Public by design: the whole point of the page is to be seen once the session
// is gone, so it lives outside the _authed subtree and carries its own gate.
//
// That gate runs the other way round from the authed layout's — an
// *authenticated* visitor is the one who is turned away, bounced into the app
// so nobody half-exists in both states. It bounces via `/`, the landing
// dispatcher, rather than naming a destination, so the hop follows the
// audience's landing path.
//
// It asks the server directly instead of going through the Query cache, and
// that is the load-bearing choice here. Every other gate wants a cached
// identity; this one wants a point-in-time answer to "is the session actually
// gone?", and the commonest arrival is one navigation after sign-out destroyed
// it — while the cache still holds the user it was destroyed for, because
// `useSignOut` clears it only once the hop has landed (so that a still-mounted
// authed page does not refire its queries against a dead session). Reading the
// cache here would bounce the visitor into the app on the very hop meant to
// show them the confirmation.
//
// Tuning `staleTime` to 0 would not be enough: `fetchQuery` hands back an
// already in-flight fetch whatever its staleTime, and a fetch begun before
// sign-out resolves to the pre-sign-out user. Only bypassing the cache is
// unconditional. The cost is one RPC on a page reached once per session.
export const Route = createFileRoute("/logout")({
  beforeLoad: async () => {
    if (await getCurrentUser()) {
      throw redirect({ to: "/", replace: true });
    }
  },
  // Per-route title, per the shell spec's head/title mechanism: without one
  // the page inherits the root's product name, and a browser-history entry
  // reading "Lennar Platform" cannot be told from any other page's.
  head: () => ({ meta: [{ title: "Logged out" }] }),
  component: LogoutPage,
});
